import { inject, Injectable } from "@angular/core"
import { Actions, createEffect, ofType } from "@ngrx/effects"
import { catchError, exhaustMap, map, tap, throwError } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http"
import { billActions } from "./bill.actions";
import { Bill, BillExtraction, BillFilters, UploadedBill, ViewedBill } from '../../models/bill.model';
import { environment } from "../../../environments/environment";
import { BillUserError } from "../../models/errors.model";
import { Store } from "@ngrx/store";
@Injectable()
export class BillsEffects {

    private httpClient = inject(HttpClient);
    private actions$ = inject(Actions);
    private store = inject(Store)

    load$ = createEffect(() => 
        this.actions$.pipe(
            ofType(billActions.loadBills),
            exhaustMap(({data}) => {
                let params: { [key: string]: any } = {};

                if(data) {
                    const filters = data as BillFilters;

                    Object.entries(filters).forEach(([key, value]) => {
                        if(value != null) {
                            if(typeof value === "string") {
                                if(value != "") {
                                    params[key] = value;
                                }
                            }
                            else {
                                params[key] = value;
                            }
                        }
                    })
                }

                const req = this.httpClient.post<Bill[]>(`${environment.api}/bill/all`, params);

                return req.pipe(
                    map((payload) => billActions.loadBillsSuccess({payload})),
                    catchError((error) => {
                        this.store.dispatch(billActions.loadBillsFail())
                        return throwError(() => new Error(error));
                    })
                )
            })
        )
    )

    download$ = createEffect(() => 
        this.actions$.pipe(
            ofType(billActions.downloadBills),
            exhaustMap(({data}) => {
                const req = this.httpClient.post<string[]>(`${environment.api}/bill/download_links`, data);

                return req.pipe(
                    map((payload) => billActions.downloadBillsSuccess({payload}))
                )
            })
        )
    )

    getCSV$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.getCSV),
            exhaustMap(({data}) => {
                const req = this.httpClient.post(`${environment.api}/bill/csv`, data, { responseType: 'text' });

                return req.pipe(
                    map((payload) => billActions.getCSVSuccess({payload}))
                )
            })
        )
    )

    getExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.getExcel),
            exhaustMap(({data}) => {
                const req = this.httpClient.post(`${environment.api}/bill/excel`, data, { responseType: 'blob' });

                return req.pipe(
                    map((payload) => billActions.getExcelSuccess({payload}))
                )
            })
        )
    )

    // upload$ = createEffect(() => 
    //     this.actions$.pipe(
    //         ofType(billActions.uploadBills),
    //         exhaustMap(({data, userId}) => {
    //             let params = new HttpParams();
    //             if(userId) {
    //                 params = params.set("bill_user_id", userId);
    //             }

    //             const req = this.httpClient.post<UploadedBill[]>(`${environment.api}/bill/multiple`, data, {params});

    //             return req.pipe(
    //                 map((payload) => billActions.uploadBillsSuccess({payload}))
    //             )
    //         })
    //     )
    // )

    getPresignedUrl$ = createEffect(() => 
        this.actions$.pipe(
            ofType(billActions.getPresignedUrl),
            exhaustMap(({filename}) => {
                let params = new HttpParams();
                params = params.set("filename", filename);

                const req = this.httpClient.get<string>(`${environment.api}/bill/insertion_url`, {params});

                return req.pipe(
                    map((payload) => billActions.getPresignedUrlSuccess({payload})),
                    catchError((error) => {
                        this.store.dispatch(billActions.getPresignedUrlFail());
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    uploadS3$ = createEffect(() => 
        this.actions$.pipe(
            ofType(billActions.uploadBillsS3),
            exhaustMap(({data, url}) => {
                let headers = new HttpHeaders();
                if (data.type) {
                    headers = headers.set('Content-Type', data.type);
                } else {
                    headers = headers.set('Content-Type', 'application/octet-stream');
                }

                console.log(headers)
                
                const req = this.httpClient.put(`${url}`, data, {headers});

                return req.pipe(
                    map((_) => billActions.uploadBillsS3Success()),
                    catchError((error) => {
                        this.store.dispatch(billActions.uploadBillsS3Fail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    uploadBackend$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.uploadBillsBackend),
            exhaustMap(({data, userId, file_type, timestamp}) => {
                let params = new HttpParams();
                if(userId) {
                    params = params.set("bill_user_id", userId);
                }
                
                let req;
                if(file_type === 'pdf') {
                    req = this.httpClient.post<UploadedBill[]>(`${environment.api}/bill/multiple/path`, data, {params});
                }
                else {
                    req = this.httpClient.post<UploadedBill[]>(`${environment.api}/bill/image/path`, {file_path: `bill/${timestamp}/`}, {params});
                }

                return req.pipe(
                    map((payload) => billActions.uploadBillsBackendSuccess({payload})),
                    catchError((error) => {
                        this.store.dispatch(billActions.uploadBillsBackendFail());
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    view$ = createEffect(() => 
        this.actions$.pipe(
            ofType(billActions.viewBill),
            exhaustMap(({data}) => {
                const req = this.httpClient.get<ViewedBill>(`${environment.api}/bill/extraction_info/${data}`);

                return req.pipe(
                    map((payload) => billActions.viewBillSuccess({payload}))
                )
            })
        )
    )

    extract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.extractBills),
            exhaustMap(({data}) => {
                const req = this.httpClient.post<BillUserError>(`${environment.api}/bill/extract_ws`, data);

                return req.pipe(
                    map((payload) => billActions.extractBillsSuccess({payload})),
                    catchError((error) => {
                        this.store.dispatch(billActions.extractBillsFail())
                        // billActions.extractBillsFail();
                        return throwError(() => new Error(error))
                    })
                )
            })
        )
    )

    deleteExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.deleteBillExtraction),
            exhaustMap(({data}) => {
                const req = this.httpClient.post(`${environment.api}/extracted_bill/delete`, data);

                return req.pipe(
                    map((_) => billActions.loadBills({data: {}})),
                )
            })
        )
    )

    add$ = createEffect(() => 
        this.actions$.pipe(
            ofType(billActions.addBill),
            exhaustMap(({data}) => {
                const req = this.httpClient.post<Bill[]>(`${environment.api}/bill`, data);

                return req.pipe(
                    map((payload) => billActions.addBillSuccess({payload}))
                )
            })
        )
    )

    edit_extraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.editBillExtraction),
            exhaustMap(({data}) => {
                const req = this.httpClient.put<BillExtraction>(`${environment.api}/bill/modify_extraction/${data.bill_id}`, data.extracted_bill);

                return req.pipe(
                    map((_) => billActions.editBillExtractionSuccess({payload: {bill_id: data.bill_id, extracted_bill: data.extracted_bill}}))
                )
            })
        )
    )

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.deleteBill),
            exhaustMap(({data}) => {
                const req = this.httpClient.post<number[]>(`${environment.api}/bill/delete`, data);

                return req.pipe(
                    map((_) => billActions.deleteBillSuccess({payload: data}))
                )
            })
        )
    )

    setBillType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(billActions.setBillType),
            exhaustMap(({billId, billType}) => {
                let params = new HttpParams();
                params = params.set("bill_id", billId);
                params = params.set("bill_type", billType);
                const req = this.httpClient.put(`${environment.api}/bill/set_bill_type/${billId}`, null, {params});

                return req.pipe(
                    map((_) => billActions.loadBills({data: {}}))
                )
            })
        )
    )
}